import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, forwardRef, inject, ChangeDetectorRef } from '@angular/core';
import { FormControl, ReactiveFormsModule, FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'ui-select',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiSelectComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <select
      [id]="id"
      [value]="modelValue ?? ''"
      (change)="onChangeEvent($event)"
      (blur)="onBlurEvent()"
      [attr.name]="name"
      [required]="required"
      [disabled]="disabled || disabledByForm"
      [attr.aria-label]="ariaLabel"
      [class]="selectClass"
    >
      <ng-content></ng-content>
    </select>
  `,
})
export class UiSelectComponent implements ControlValueAccessor {
  @Input() id?: string;
  @Input() value?: string | number | null;
  @Input() name?: string;
  @Input() required = false;
  @Input() disabled = false;
  @Input() ariaLabel?: string;
  @Input() selectClass = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white';
  @Input() formControl?: FormControl;
  @Input() ngModel?: any;
  @Output() ngModelChange = new EventEmitter<any>();
  @Output() change = new EventEmitter<Event>();

  modelValue: any = '';
  disabledByForm = false;
  private cdr = inject(ChangeDetectorRef);
  private onChangeFn: (value: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  writeValue(value: any): void {
    this.modelValue = value ?? '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByForm = isDisabled;
  }

  onChangeEvent(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.modelValue = value;
    this.ngModelChange.emit(value);
    this.onChangeFn(value);
    this.change.emit(event);
  }

  onBlurEvent(): void {
    this.onTouchedFn();
  }
}
